import {
  type PaginationState,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'

import { type Broker } from 'components/brokers/types'
import BrokerContext from 'components/context/BrokerContext'
import ReimbursementsContext from 'components/context/ReimbursementsContext'
import {
  getReimbursementsQueryKey,
  type ReimbursementsFilters,
} from 'components/hooks/reimbursements/useGetReimbursementRequests'
import RowSelectionCountDisplay from 'components/reusable/table/RowSelectionCountDisplay'
import { Button } from 'slab'

import BulkUpdateReimbursementsDialog from './BulkUpdateReimbursementsDialog'
import ReimbursementRequestFilters, {
  filterConfigurations,
  type ReimbursementFilterGroupType,
} from './ReimbursementRequestFilters'
import ReimbursementsQuickFilters from './ReimbursementsQuickFilters'
import ReimbursementsTable, {
  MAX_NUMBER_OF_SELECTED_ITEMS,
} from './ReimbursementsTable'
import SyncFiltersWithURL from './SyncFiltersWithUrl'
import { type Reimbursement, type User } from './types'

type ReimbursementRequestsViewProps = {
  currentUser: User
  currentBroker?: Broker
}

const ReimbursementRequestsViewImpl = ({
  currentUser,
  currentBroker,
}: ReimbursementRequestsViewProps) => {
  const [filters, setFilters] = useState<ReimbursementsFilters>({})
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // Reset row selection when filters and/or sorting change. This is to prevent the user from bulk updating items no
  // longer visible on the page.
  useEffect(() => {
    setRowSelection({})
  }, [pagination.pageSize, sorting, JSON.stringify(filters)])

  const numberOfSelectedItems =
    Object.values(rowSelection).filter(Boolean).length

  return (
    <ReimbursementsContext.Provider
      value={{
        setFilters,
        currentUser,
        getReimbursementsQueryKey: getReimbursementsQueryKey({
          filters,
          pagination,
          sorting,
        }),
      }}
    >
      <BrokerContext.Provider value={{ currentBroker }}>
        <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2000px] mx-auto'>
          <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
            <div className='text-2xl font-semibold w-96'>Reimbursements</div>
          </div>
          <SyncFiltersWithURL<
            ReimbursementsFilters,
            Reimbursement,
            ReimbursementFilterGroupType
          >
            filters={filters}
            pagination={pagination}
            setFilters={setFilters}
            defaultFilters={{ states: ['waiting_for_review'] }}
            sortableKeys={['created_at', 'state']}
            filterConfigurations={filterConfigurations}
          />
          <ReimbursementsQuickFilters
            filters={filters}
            setFilters={setFilters}
          />
          <ReimbursementRequestFilters
            filters={filters}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
          />
          <BulkUpdateReimbursementsDialog
            isOpen={isBulkUpdateOpen}
            setIsOpen={setIsBulkUpdateOpen}
            selectedItems={Object.entries(rowSelection).reduce<string[]>(
              (acc, [key, value]) => (value ? [...acc, key] : acc),
              []
            )}
          />
          <div className='h-10 mb-2 flex items-center justify-between w-full'>
            <RowSelectionCountDisplay
              numberOfSelectedItems={numberOfSelectedItems}
              setRowSelection={setRowSelection}
              maxNumberOfSelectedItems={MAX_NUMBER_OF_SELECTED_ITEMS}
            />
            <Button
              disabled={numberOfSelectedItems === 0}
              onClick={() => setIsBulkUpdateOpen(true)}
              variant='primary'
            >
              Update Selected
            </Button>
          </div>
          <ReimbursementsTable
            filters={filters}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            currentUser={currentUser}
            currentBroker={currentBroker}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
        </div>
      </BrokerContext.Provider>
    </ReimbursementsContext.Provider>
  )
}

const ReimbursementRequestsView = (props: ReimbursementRequestsViewProps) => {
  return (
    <ReimbursementRequestsViewImpl {...props} />
  )
}

export default ReimbursementRequestsView
